import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";

import { useContainerQuery } from "@transfr-inc/dashboard-components/container-query";

import { LOGOUT_TYPES } from "@models/tracking/event-types";
import { DeauthenticateUser } from "@models/tracking/events";

export const SEC_IN_MINUTE = 60;
export const SEC_IN_HOUR = 60 * SEC_IN_MINUTE;
export const SEC_IN_DAY = 24 * SEC_IN_HOUR;

export function useQueryString() {
  const { search, pathname } = useLocation();
  const queryParams = useMemo(() => new URLSearchParams(search), [search]);

  const getCurrentPath = () => {
    return `${pathname}${queryParams.size > 0 ? "?" + queryParams : ""}`;
  };

  return {
    queryParams,
    getCurrentPath,
  };
}

export function useTimer(formatOptions) {
  const [timeLeft, setTimeLeft] = useState();
  const [isDone, setIsDone] = useState(false);
  const [timeFormatted, setTimeFormatted] = useState();

  useEffect(() => {
    const intervalId = setInterval(() => {
      setTimeLeft((t) => (t > 0 ? t - 1 : t));
    }, 1000);

    return () => clearInterval(intervalId);
  }, []);

  useEffect(() => {
    setIsDone(timeLeft <= 0);
    setTimeFormatted(formatTime(timeLeft ?? 0, formatOptions));
  }, [timeLeft]);

  const setTimer = (time) => setTimeLeft(Math.trunc(time));

  return {
    setTimer,
    timeLeft,
    isDone,
    timeFormatted,
  };
}

const pad2 = (value) => (value < 10 ? `0${value}` : `${value}`);

/**
 * Format a time duration:
 * - >= 1 day: DDd HHh
 * - >= 1 hour and < 1 day: HHh MMm
 * - < 1 hour: MMm SSs
 * If compact is true, returns:
 * - >= 1 day: DD:HH
 * - >= 1 hour and < 1 day: HH:MM
 * - < 1 hour: MM:SS
 */
const formatTime = (seconds, { compact = false } = {}) => {
  const totalSeconds = Math.max(0, Math.trunc(seconds ?? 0));

  if (totalSeconds >= SEC_IN_DAY) {
    const days = Math.floor(totalSeconds / SEC_IN_DAY);
    const hours = Math.floor((totalSeconds % SEC_IN_DAY) / SEC_IN_HOUR);
    return compact
      ? `${pad2(days)}:${pad2(hours)}`
      : `${pad2(days)}d ${pad2(hours)}h`;
  }

  if (totalSeconds >= SEC_IN_HOUR) {
    const hours = Math.floor(totalSeconds / SEC_IN_HOUR);
    const minutes = Math.floor((totalSeconds % SEC_IN_HOUR) / SEC_IN_MINUTE);
    return compact
      ? `${pad2(hours)}:${pad2(minutes)}`
      : `${pad2(hours)}h ${pad2(minutes)}m`;
  }

  const minutes = Math.floor(totalSeconds / SEC_IN_MINUTE);
  const secs = totalSeconds % SEC_IN_MINUTE;
  return compact
    ? `${pad2(minutes)}:${pad2(secs)}`
    : `${pad2(minutes)}m ${pad2(secs)}s`;
};

export const useNotificationStack = (initialValue = []) => {
  const idCounter = useRef(0);
  const [notifications, setNotifications] = useState(initialValue);

  const closeNotification = ({ index, id }) => {
    setNotifications((value) =>
      value.filter((n, i) => (id ? n.id !== id : i !== index))
    );
  };

  const addNotification = ({
    type,
    message,
    icon,
    iconConfig,
    closable,
    className,
    autoClose = true,
  }) => {
    const id = `notification_${type}_${idCounter.current}`;

    setNotifications((n) => {
      const notificationsUpdated = n ?? [];
      // NotificationStack spreads each entry onto its Notification after its
      // own defaults, so a field set here overrides the stack-level default
      // for this one toast (e.g. closable:false for a non-dismissible toast,
      // or a spinning icon via iconConfig). A key that is present but
      // undefined still clobbers the stack-level prop, so drop unset fields.
      const entry = {
        type,
        message,
        icon,
        iconConfig,
        closable,
        className,
        id,
      };
      for (const key of Object.keys(entry)) {
        if (entry[key] === undefined) {
          delete entry[key];
        }
      }
      return [...notificationsUpdated, entry];
    });

    idCounter.current += 1;
    if (autoClose) {
      closeNotificationTimeout(id, 10_000);
    }
    return id;
  };

  const closeNotificationTimeout = (notificationId, timeout = 3000) => {
    const timeoutId = setTimeout(() => {
      closeNotification({ id: notificationId });
    }, timeout);

    return () => clearTimeout(timeoutId);
  };

  return {
    notifications,
    closeNotification,
    closeNotificationTimeout,
    addNotification,
  };
};

export const useSessionExpirationTracking = (currentUser, trackingService) => {
  const hasTrackedExpiration = useRef(false);
  const timerRef = useRef();
  const MINUTE_MS = 60000;

  useEffect(() => {
    const checkSession = () => {
      const currentTime = Math.floor(Date.now() / 1000);
      if (
        currentUser?.exp &&
        currentTime >= currentUser.exp &&
        !hasTrackedExpiration.current
      ) {
        trackingService.trackEvent(
          new DeauthenticateUser(LOGOUT_TYPES.CORE_PLATFORM)
        );
        hasTrackedExpiration.current = true;
        if (timerRef.current) {
          clearInterval(timerRef.current);
        }
      }
    };

    if (!hasTrackedExpiration.current) {
      checkSession();
      timerRef.current = setInterval(checkSession, MINUTE_MS);

      return () => {
        if (timerRef.current) {
          clearInterval(timerRef.current);
        }
      };
    }
  }, []);
};

const defaultQuery = {
  "small-panel": {
    minWidth: 320,
    maxWidth: 767,
  },
  "medium-panel": {
    minWidth: 768,
    maxWidth: 1080,
  },
  "large-panel": {
    minWidth: 1080,
  },
};

export const useResponsiveStyles = (query) => {
  const containerQuery = useMemo(
    () => ({
      ...defaultQuery,
      ...query,
    }),
    [query]
  );

  const [params, containerRef] = useContainerQuery(containerQuery);

  return {
    responsiveStyles: params,
    containerRef,
  };
};
